import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { Card, Col, Form, Row, Dropdown } from 'react-bootstrap'
import Cookies from 'js-cookie'
import { useNavigate } from 'react-router-dom'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import {
  BsArrowClockwise,
  BsEyeFill,
  BsFileEarmarkPdfFill,
  BsFileEarmarkSpreadsheetFill,
  BsFunnelFill,
  BsSearch,
  BsThreeDotsVertical,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import toast from 'react-hot-toast'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'

export default function LeftTeachers() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const instituteId = Cookies.get('institute_id')
  const { logo, name } = useInstitute()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [searchName, setSearchName] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [searchDesignation, setSearchDesignation] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchData = async () => {
    try {
      const userCookie = Cookies.get('user')
      const parsedUser = userCookie ? JSON.parse(userCookie) : null
      const token = parsedUser?.token

      const response = await axios.get(`${BASE_URL}/teacher/inactive`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Institute-Id': Number(instituteId),
          Accept: 'application/json',
        },
      })

      const fetchedData = Array.isArray(response.data?.data)
        ? response.data.data
        : []

      setData(fetchedData)
      setFilteredData(fetchedData)
    } catch (error) {
      const message =
        error.response?.data?.message || 'Failed to fetch inactive teachers'
      toast.error(message)
      setData([])
      setFilteredData([])
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    let filtered = data

    if (searchName.trim() !== '') {
      filtered = filtered.filter((teacher) =>
        teacher.name?.toLowerCase().includes(searchName.toLowerCase()),
      )
    }

    if (searchPhone.trim() !== '') {
      filtered = filtered.filter((teacher) =>
        teacher.phone?.toString().includes(searchPhone),
      )
    }

    if (searchDesignation.trim() !== '') {
      filtered = filtered.filter((teacher) =>
        teacher.designation
          ?.toLowerCase()
          .includes(searchDesignation.toLowerCase()),
      )
    }

    setFilteredData(filtered)
  }, [searchName, searchPhone, searchDesignation, data])

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [filteredData, itemsPerPage, totalPages, currentPage])

  const handleView = (teacher) => {
    if (!teacher?.id) {
      return
    }

    navigate(`/TeacherProfile/${teacher.id}`, {
      state: { teacher, fromLeftTeachers: true },
    })
  }

  const handleRefresh = () => {
    setSearchName('')
    setSearchPhone('')
    setSearchDesignation('')
    setCurrentPage(1)
    fetchData()
  }

  const canExportTeachers =
    user?.user_type === 'admin' || user?.roles?.includes('admin')

  const getExportRows = () =>
    filteredData.map((teacher) => ({
      Name: teacher.name ?? '',
      Email: teacher.email ?? '',
      Phone: teacher.phone ?? '',
      Designation: teacher.designation ?? '',
      Qualification: teacher.qualification ?? '',
      'Joining Date': teacher.joining_date ?? '',
      Status: teacher.status ?? '',
      Address: teacher.address ?? '',
    }))

  const exportToExcel = () => {
    const exportData = getExportRows()
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Left Teachers')
    XLSX.writeFile(workbook, 'Left_Teacher_Records.xlsx')
  }

  const formatDate = (value) => {
    if (!value) return 'N/A'
    try {
      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return String(value)
    }
  }

  const handleSaveAsPDF = () => {
    const rows = getExportRows()
    if (rows.length === 0) return

    try {
      const doc = new jsPDF('l', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 10
      const rowHeight = 8
      let yPos = 22

      const headers = [
        'Name',
        'Email',
        'Phone',
        'Designation',
        'Qualification',
        'Joining Date',
      ]
      const colWidths = [40, 60, 32, 48, 42, 42]

      const drawTableHeader = () => {
        let xPos = margin
        doc.setFillColor(34, 56, 67)
        doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        headers.forEach((header, index) => {
          doc.text(header, xPos + 1.5, yPos + 5.5)
          xPos += colWidths[index]
        })
        yPos += rowHeight
      }

      const fitText = (text, maxLength = 24) => {
        const safeText = String(text || '')
        if (safeText.length <= maxLength) return safeText
        return `${safeText.slice(0, maxLength - 3)}...`
      }

      const instituteDisplayName = String(
        name || Cookies.get('name') || Cookies.get('institute_name') || '',
      ).trim()

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67)
      doc.text(instituteDisplayName || 'Institute', pageWidth / 2, 10, {
        align: 'center',
      })

      doc.setFontSize(12)
      doc.text('Left Teachers', pageWidth / 2, 16, {
        align: 'center',
      })

      drawTableHeader()

      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)
      doc.setFontSize(8.5)

      rows.forEach((rowObj, index) => {
        if (yPos + rowHeight > pageHeight - margin) {
          doc.addPage()
          yPos = 14
          drawTableHeader()
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(17, 24, 39)
          doc.setFontSize(8.5)
        }

        let xPos = margin
        const rowData = [
          fitText(rowObj.Name, 18),
          fitText(rowObj.Email, 30),
          fitText(rowObj.Phone, 14),
          fitText(rowObj.Designation, 22),
          fitText(rowObj.Qualification, 18),
          formatDate(rowObj['Joining Date']),
        ]

        if (index % 2 !== 0) {
          doc.setFillColor(249, 250, 251)
          doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        }

        rowData.forEach((cell, colIndex) => {
          doc.text(String(cell), xPos + 1.5, yPos + 5.5)
          xPos += colWidths[colIndex]
        })

        yPos += rowHeight
      })

      doc.save('Left_Teacher_Records.pdf')
    } catch (error) {
      console.error('Error while generating PDF:', error)
      toast.error('Failed to generate PDF')
    }
  }

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }

          .table-card {
            overflow: visible !important;
            position: relative;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
            max-height: none !important;
          }
          .table,
          .table tbody tr,
          .table td {
            overflow: visible !important;
          }
          .table td {
            position: relative;
          }
          .dropdown-menu,
          .dropdown-menu.show,
          .dropdown-menu[data-popper-placement] {
            z-index: 1050 !important;
            position: fixed !important;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Left Teachers
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4',
                }}
              >
                <span
                  style={{
                    color: '#6b7280',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value, 10))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>

              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>

              {canExportTeachers && filteredData.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleSaveAsPDF}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#EF4444',
                      border: 'none',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    title="Save as PDF"
                  >
                    <BsFileEarmarkPdfFill size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={exportToExcel}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#10B981',
                      border: 'none',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    title="Export to Excel"
                  >
                    <BsFileEarmarkSpreadsheetFill size={20} />
                  </button>
                </>
              )}
            </div>
          </div>

          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5
                          className="m-0 fw-semibold text-start"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Search & Filter Teachers
                        </h5>
                        <p
                          className="m-0 mt-1"
                          style={{ fontSize: '13px', color: '#6b7280' }}
                        >
                          Find inactive teachers quickly with focused filters
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRefresh}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: '#223843',
                        border: 'none',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      title="Refresh"
                    >
                      <BsArrowClockwise size={20} />
                    </button>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-4 text-start">
                    <Col xs={12}>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BsSearch style={{ color: '#ffffff', fontSize: '16px' }} />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Filter by Teacher Details
                          </label>
                        </div>
                        <Row className="g-3">
                          <Col xs={12} md={4}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Search Teacher Name
                            </label>
                            <Form.Control
                              type="text"
                              value={searchName}
                              onChange={(e) => setSearchName(e.target.value)}
                              placeholder="Enter teacher name..."
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #D1D3D4',
                                fontSize: '14px',
                                height: '40px',
                                backgroundColor: '#ffffff',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            />
                          </Col>
                          <Col xs={12} md={4}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Search Phone Number
                            </label>
                            <Form.Control
                              type="text"
                              value={searchPhone}
                              onChange={(e) => setSearchPhone(e.target.value)}
                              placeholder="Enter phone number..."
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #D1D3D4',
                                fontSize: '14px',
                                height: '40px',
                                backgroundColor: '#ffffff',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            />
                          </Col>
                          <Col xs={12} md={4}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Search Designation
                            </label>
                            <Form.Control
                              type="text"
                              value={searchDesignation}
                              onChange={(e) => setSearchDesignation(e.target.value)}
                              placeholder="Enter designation..."
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #D1D3D4',
                                fontSize: '14px',
                                height: '40px',
                                backgroundColor: '#ffffff',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            />
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          <Card
            className="table-card"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'visible',
              backgroundColor: '#ffffff',
            }}
          >
            <div className="table-wrapper" style={{ borderRadius: '12px' }}>
              <Table className="table mb-0" style={{ borderRadius: '12px' }}>
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th style={headerCellStyle}>Name</Th>
                    <Th style={headerCellStyle}>Email</Th>
                    <Th style={headerCellStyle}>Phone</Th>
                    <Th style={headerCellStyle}>Designation</Th>
                    <Th style={headerCellStyle}>Qualification</Th>
                    <Th style={headerCellStyle}>Joining Date</Th>
                    <Th style={headerCellStyle}>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredData.length > 0 ? (
                    currentItems.map((teacher, index) => (
                      <Tr
                        key={teacher.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#F3F4F6',
                          transition: 'background-color 0.2s ease',
                          borderBottom: '1px solid #D1D3D4',
                        }}
                      >
                        <Td style={bodyCellStyle}>{teacher.name || 'N/A'}</Td>
                        <Td style={bodyCellStyle}>{teacher.email || 'N/A'}</Td>
                        <Td style={bodyCellStyle}>{teacher.phone || 'N/A'}</Td>
                        <Td style={bodyCellStyle}>{teacher.designation || 'N/A'}</Td>
                        <Td style={bodyCellStyle}>
                          {teacher.qualification || 'N/A'}
                        </Td>
                        <Td style={bodyCellStyle}>
                          {formatDate(teacher.joining_date)}
                        </Td>
                        <Td className="text-center" style={{ padding: '16px', border: 'none' }}>
                          <div className="d-none d-md-block">
                            <Dropdown
                              popperConfig={{
                                modifiers: [
                                  {
                                    name: 'preventOverflow',
                                    options: { boundary: 'viewport', padding: 8 },
                                  },
                                  {
                                    name: 'flip',
                                    options: {
                                      fallbackPlacements: [
                                        'top',
                                        'bottom',
                                        'left',
                                        'right',
                                      ],
                                      boundary: 'viewport',
                                      padding: 8,
                                    },
                                  },
                                  {
                                    name: 'offset',
                                    options: { offset: [0, 8] },
                                  },
                                ],
                                strategy: 'fixed',
                              }}
                            >
                              <Dropdown.Toggle
                                variant="light"
                                id={`dropdown-${teacher.id}`}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: 'transparent',
                                  border: '1px solid #D1D3D4',
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                className="dropdown-toggle-no-caret"
                              >
                                <BsThreeDotsVertical style={{ color: 'inherit' }} />
                              </Dropdown.Toggle>

                              <Dropdown.Menu
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #D1D3D4',
                                  padding: '8px',
                                  minWidth: '180px',
                                }}
                              >
                                <Dropdown.Item
                                  onClick={() => handleView(teacher)}
                                  style={{
                                    fontSize: '13px',
                                    padding: '10px 12px',
                                    color: '#111827',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    borderRadius: '6px',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  <BsEyeFill size={18} /> See Profile
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>

                          <div className="d-md-none d-flex justify-content-start gap-2">
                            <button
                              onClick={() => handleView(teacher)}
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: '#223843',
                                border: 'none',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                              title="See Profile"
                            >
                              <BsEyeFill size={16} />
                            </button>
                          </div>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td
                        colSpan="7"
                        className="text-center"
                        style={{
                          padding: '40px',
                          fontSize: '14px',
                          color: '#6b7280',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        No records found
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
          </Card>

          <TablePagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            totalItems={filteredData.length}
            itemLabel="teachers"
          />
        </div>
      </div>
    </>
  )
}

const headerCellStyle = {
  fontSize: '13px',
  fontWeight: '600',
  padding: '16px',
  border: 'none',
  borderBottom: '1px solid #D1D3D4',
  textAlign: 'left',
  color: '#111827',
  fontFamily: 'Inter, system-ui, sans-serif',
}

const bodyCellStyle = {
  fontSize: '14px',
  padding: '16px',
  border: 'none',
  color: '#111827',
  textAlign: 'left',
  fontFamily: 'Inter, system-ui, sans-serif',
}
